import { useEffect, useRef, useState } from "react"
import { Flex, List } from "@chakra-ui/react"
import { useSession } from "next-auth/react"
import { useGlobalState } from "providers/globalState"
import { getAccessibleChannels } from "src/util/user"
import { filterMenuByUserStatus } from "util/helpers"
import MenuItem from "components/nav/menuItem"
import LoginModal from "components/login/loginModal"
import stripeMenu from "config/stripe"
import useDimensions from "hooks/useDimensions"

export default function Nav({ menu, auxiliaryMenu, setMenuIsTooWideForWindow, showMobileMenu, mobileMenuToggle }) {
    const { data: session, status } = useSession()
    const { site, locale } = useGlobalState()

    const [loginModalIsOpen, setLoginModalIsOpen] = useState(false)

    const navRef = useRef()
    const dimensions = useDimensions(navRef)
    const debounceTimerRef = useRef(null)
    const desktopMenuWidthRef = useRef(null)

    const user = session?.user

    const menuLength = menu.filter(item => {
        return item.parent == null
    }).length

    useEffect(() => {
        if (!dimensions?.borderBox) return

        const updateWindowDimensions = () => {
            // Clear any pending updates
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }

            // Debounce the state update to prevent rapid toggling
            debounceTimerRef.current = setTimeout(() => {
                const menuWidth = dimensions?.borderBox.width
                const windowWidth = window.innerWidth

                if (showMobileMenu) {
                    // Currently in mobile mode (vertical menu is narrow)
                    // We need to use the stored desktop width to decide if we can switch back
                    if (desktopMenuWidthRef.current) {
                        // Only switch back to desktop if the stored desktop width fits
                        const shouldStayMobile = desktopMenuWidthRef.current > windowWidth
                        setMenuIsTooWideForWindow(shouldStayMobile)
                    }
                    // If we don't have a stored width, stay in mobile mode
                } else {
                    // Currently in desktop mode (horizontal menu) - store this width
                    desktopMenuWidthRef.current = menuWidth

                    // Switch to mobile if too wide
                    const shouldSwitchToMobile = menuWidth > windowWidth
                    setMenuIsTooWideForWindow(shouldSwitchToMobile)
                }
            }, 100)
        }

        updateWindowDimensions()

        window.addEventListener("resize", updateWindowDimensions)

        return () => {
            window.removeEventListener("resize", updateWindowDimensions)
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [dimensions, setMenuIsTooWideForWindow, showMobileMenu, menuLength])

    // Don't render menu until session has finished loading
    if (status === "loading") {
        return null
    }

    menu = filterMenuByUserStatus(menu, user)

    const versionLabels = {
        live: "Live",
        ondemand: "On Demand",
    }

    // build Channels/ menu (only after session has finished loading)
    const channelsMenuIndex = menu.findIndex(item => item.typeHandle === "channels")
    const channelsMenu = menu[channelsMenuIndex]

    if (channelsMenuIndex > -1) {
        const userAccessibleChannels = getAccessibleChannels(user, site, locale)
        const channelsMenuPages = userAccessibleChannels.length
            ? generateChannelsMenuItems(channelsMenu, userAccessibleChannels)
            : []

        if (!channelsMenuPages.length) {
            menu.splice(channelsMenuIndex, 1)
        } else {
            // Create the "All" menu item
            const sessionTypeTerm = site.meeting.sessionTypeTerm || "Channel"
            const allChannelsMenuItem = {
                title: "All " + sessionTypeTerm + "s",
                typeHandle: "path",
                navigationPath: "portal",
            }
            // insert channel items above any existing menu items
            channelsMenu.children = [allChannelsMenuItem, ...channelsMenuPages, ...channelsMenu.pages]
        }
    }

    function generateChannelsMenuItems(menuItem, channels) {
        return channels.flatMap(channel => {
            // generate menu items for available channels
            const channelSubPages = channel.currentlyAvailableVersions.map(version => {
                return {
                    title: versionLabels[version],
                    typeHandle: "path",
                    navigationPath: `${version}/${channel.slug}`,
                }
            })

            // add subhead
            if (channelSubPages.length) {
                channelSubPages.unshift({
                    title: channel.title,
                    typeHandle: "subhead",
                })
            }

            return channelSubPages
        })
    }

    const actions = {
        login: () => {
            setLoginModalIsOpen(true)
        },
    }

    const doAction = action => {
        actions[action].call()
    }

    const crfmenu = [
        {
            title: "Cardiovascular Research Foundation",
            typeHandle: "null",
            bgColor: "#e7e7e7",
            grayscale: true,
            pages: stripeMenu.sites.map(site => {
                return {
                    ...site,
                    string: site.url,
                    typeHandle: "link",
                    grayscale: true,
                }
            }),
        },
        {
            title: "CRF Meetings",
            typeHandle: "null",
            bgColor: "#e7e7e7",
            grayscale: true,
            pages: stripeMenu.meetings.map(site => {
                return {
                    ...site,
                    string: site.url,
                    typeHandle: "link",
                    grayscale: true,
                }
            }),
        },
    ]

    return (
        <>
            <Flex as={List.Root} ref={navRef} className="menu__items" direction={showMobileMenu ? "column" : "row"}>
                {menu.map((item, i) => {
                    return (
                        <MenuItem
                            key={i}
                            item={item}
                            level={0}
                            isLast={menu.length === i + 1}
                            doAction={doAction}
                            isMobile={showMobileMenu}
                            mobileMenuToggle={mobileMenuToggle}
                        />
                    )
                })}

                {auxiliaryMenu}

                {showMobileMenu &&
                    crfmenu.map((item, i) => {
                        return (
                            <MenuItem
                                className={"crfmenu-menu-item"}
                                key={i}
                                item={item}
                                level={0}
                                isLast={crfmenu.length === i + 1}
                                doAction={doAction}
                                isMobile={showMobileMenu}
                            />
                        )
                    })}
            </Flex>

            {loginModalIsOpen && <LoginModal onClose={setLoginModalIsOpen} />}
        </>
    )
}
